/**
 * TỰ KIỂM TRA ĐIỀU KIỆN CHẤM CÔNG.
 *
 * Trả lời đúng một câu: *"nếu tôi bấm chấm công BÂY GIỜ thì hệ thống thấy
 * gì?"* — trước khi nó thành một bản ghi bị từ chối mà không ai hiểu vì sao.
 *
 * Gộp hai phía, vì lỗi nằm rải ở cả hai và không phía nào tự thấy được phía
 * kia:
 *
 *   TRÌNH DUYỆT  secure context · camera · quyền vị trí
 *   MÁY CHỦ      IP nguồn thấy được · có nhận ra mạng công ty không ·
 *                khuôn mặt đã đăng ký · thiết bị đã gắn · ca đang dùng
 *
 * Đặc biệt cần khi chuyển sang tên miền: thiếu DNS nội bộ thì điện thoại
 * trong văn phòng đi vòng ra Internet rồi quay lại, IP nguồn thành IP public
 * và tín hiệu mạng công ty (+30 điểm) biến mất mà không có thông báo nào.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../api/loi'
import { layTuKiemTra } from '../services/attendance.service'
import { kiemTraTruoc, laNguCanhBaoMat } from '../lib/camera'

interface KetQuaServer {
  mang: {
    ip_nguon: string | null
    trang_thai: string
    cach_xac_minh: string
    trong_van_phong: boolean
    dia_diem_khop: string | null
    ly_do: string[]
  }
  nhan_vien: {
    da_ky_dong_y: boolean
    so_mau_khuon_mat: number
    thiet_bi_da_gan: string | null
  }
  ca_dang_dung: { id: number; name: string; start_time: string; end_time: string }[]
  dia_diem: { id: number; name: string; kind: string; co_lan_cidr: boolean }[]
  canh_bao: string[]
}

const NHAN_MANG: Record<string, string> = {
  OFFICE_TRUSTED: 'Mạng công ty — thiết bị tin cậy',
  OFFICE_CONTROLLER_VERIFIED: 'Mạng công ty — controller xác nhận',
  OFFICE_LAN: 'Dải mạng công ty',
  EXTERNAL_WIFI: 'WiFi ngoài công ty',
  EXTERNAL_MOBILE: 'Mạng di động (4G/5G)',
  UNKNOWN: 'Không xác định',
  VERIFICATION_FAILED: 'Không xác minh được',
}

function Dong({ nhan, dat, gia_tri, ghi_chu }: {
  nhan: string
  /** true đạt · false hỏng · null không áp dụng / chỉ là thông tin */
  dat: boolean | null
  gia_tri?: string
  ghi_chu?: string
}) {
  return (
    <div className="kt-dong">
      <span className="kt-dau" aria-hidden="true">
        {dat === null ? '·' : dat ? '✓' : '✕'}
      </span>
      <div>
        <div className="kt-nhan">
          {nhan}
          {gia_tri && <span className="kt-gia-tri"> {gia_tri}</span>}
        </div>
        {ghi_chu && <div className="kt-ghi-chu">{ghi_chu}</div>}
      </div>
    </div>
  )
}

export default function TuKiemTra() {
  const [kq, setKq] = useState<KetQuaServer | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [dangTai, setDangTai] = useState(false)
  const [quyenViTri, setQuyenViTri] = useState<string>('chưa rõ')

  const camera = kiemTraTruoc()
  const baoMat = laNguCanhBaoMat()

  const nap = useCallback(async () => {
    setDangTai(true)
    setLoi(null)
    try {
      setKq(await layTuKiemTra<KetQuaServer>())
    } catch (e) {
      setLoi(e instanceof ApiError ? e.message : 'Không hỏi được máy chủ')
    } finally {
      setDangTai(false)
    }
  }, [])

  useEffect(() => {
    void nap()
    // `permissions` không có trên Safari đời cũ — nuốt lỗi, chỉ là thông tin.
    navigator.permissions?.query({ name: 'geolocation' as PermissionName })
      .then((p) => setQuyenViTri(p.state))
      .catch(() => setQuyenViTri('không hỏi được'))
  }, [nap])

  return (
    <div className="tu-kiem-tra">
      <div className="cum-nut">
        <button type="button" className="nut-phu" onClick={() => void nap()} disabled={dangTai}>
          {dangTai ? 'Đang kiểm tra…' : 'Kiểm tra lại'}
        </button>
      </div>

      {loi && <p className="thong-bao-loi" role="alert">{loi}</p>}

      <h3 className="kt-tieu-de">Trình duyệt</h3>
      <Dong
        nhan="Kết nối bảo mật (HTTPS)"
        dat={baoMat}
        gia_tri={window.location.protocol.replace(':', '')}
        ghi_chu={baoMat
          ? undefined
          : 'Thiếu HTTPS thì KHÔNG mở được camera, không lấy được vị trí. '
            + 'Mở lại trang bằng địa chỉ https://'}
      />
      <Dong
        nhan="Camera"
        dat={camera.ly_do === 'ok'}
        ghi_chu={camera.ly_do === 'ok' ? undefined : camera.thong_diep}
      />
      <Dong nhan="Quyền vị trí" dat={null} gia_tri={quyenViTri}
            ghi_chu="Không bắt buộc — thiếu GPS vẫn chấm công được nếu ở mạng công ty." />

      {kq && (
        <>
          <h3 className="kt-tieu-de">Máy chủ nhìn thấy</h3>
          <Dong
            nhan="Mạng"
            dat={kq.mang.trong_van_phong ? true : null}
            gia_tri={NHAN_MANG[kq.mang.trang_thai] ?? kq.mang.trang_thai}
            ghi_chu={kq.mang.trong_van_phong
              ? `Địa điểm khớp: ${kq.mang.dia_diem_khop ?? '—'} · đủ điều kiện chấm công không cần GPS`
              : 'Ngoài mạng công ty — cần GPS trong phạm vi công trình, hoặc mã tổ trưởng, kèm ảnh khuôn mặt.'}
          />
          <Dong nhan="IP máy chủ thấy" dat={null} gia_tri={kq.mang.ip_nguon ?? '—'} />
          <Dong
            nhan="Đồng ý sinh trắc học"
            dat={kq.nhan_vien.da_ky_dong_y}
            ghi_chu={kq.nhan_vien.da_ky_dong_y ? undefined : 'Vào mục Khuôn mặt để ký.'}
          />
          <Dong
            nhan="Mẫu khuôn mặt"
            dat={kq.nhan_vien.so_mau_khuon_mat > 0}
            gia_tri={`${kq.nhan_vien.so_mau_khuon_mat}/3`}
            ghi_chu={kq.nhan_vien.so_mau_khuon_mat > 0
              ? undefined
              : 'Chưa đăng ký — chấm công nơi bắt buộc Face ID sẽ bị từ chối.'}
          />
          <Dong
            nhan="Thiết bị đã gắn"
            dat={kq.nhan_vien.thiet_bi_da_gan !== null}
            ghi_chu={kq.nhan_vien.thiet_bi_da_gan === null
              ? 'Lần chấm công đầu sẽ tự gắn máy này.'
              : undefined}
          />
          <Dong
            nhan="Ca làm việc đang dùng"
            dat={kq.ca_dang_dung.length > 0}
            gia_tri={`${kq.ca_dang_dung.length} ca`}
            ghi_chu={kq.ca_dang_dung.map((c) => `${c.name} ${c.start_time}–${c.end_time}`).join(' · ')}
          />

          {kq.canh_bao.length > 0 && (
            <div className="thong-bao-loi" role="alert">
              <strong>Cần xử lý:</strong>
              <ul>{kq.canh_bao.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}
